/**
 * CustomerForm - Reusable form component for creating/editing customers
 */

import React from "react";
import { Form, Input } from "antd";
import { UserOutlined, PhoneOutlined, MailOutlined } from "@ant-design/icons";

const { TextArea } = Input;

/**
 * Validate South African phone number
 * Accepts various formats including spaces, dashes, and parentheses
 * Uses the same logic as backend formatPhoneNumber/isValidSouthAfricanPhone
 */
const validateSouthAfricanPhone = (_, value) => {
  if (!value) {
    // Phone is optional
    return Promise.resolve();
  }

  // Remove all non-digit characters for normalization
  const digits = value.replace(/\D/g, "");

  // Normalize to +27XXXXXXXXX format (same as backend formatPhoneNumber)
  let normalized;
  if (digits.length === 10 && digits.startsWith("0")) {
    // Convert 0712345678 to +27123456789
    normalized = `+27${digits.substring(1)}`;
  } else if (digits.length === 11 && digits.startsWith("27")) {
    // Convert 27123456789 to +27123456789
    normalized = `+${digits}`;
  } else if (digits.length === 9 && !digits.startsWith("0")) {
    // Convert 123456789 to +27123456789
    normalized = `+27${digits}`;
  } else if (value.startsWith("+27")) {
    // Already in correct format, but ensure it's clean
    normalized = `+27${digits.substring(2)}`; // Remove +27 and re-add with clean digits
  } else {
    // Invalid format
    return Promise.reject(
      new Error("Please enter a valid South African phone number (e.g., +27123456789 or 0712345678)")
    );
  }

  // Validate normalized format: +27 followed by exactly 9 digits
  const isValid = /^\+27[0-9]{9}$/.test(normalized);

  if (!isValid) {
    return Promise.reject(
      new Error("Please enter a valid South African phone number (e.g., +27123456789 or 0712345678)")
    );
  }

  return Promise.resolve();
};

const CustomerForm = ({ form, onFinish, loading = false, initialValues = {}, onValuesChange }) => {
  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={onFinish}
      initialValues={initialValues}
      onValuesChange={onValuesChange}
    >
      <Form.Item
        name="name"
        label="Customer Name"
        rules={[
          { required: true, message: "Customer name is required" },
          { min: 2, message: "Name must be at least 2 characters" },
          { max: 200, message: "Name must be less than 200 characters" },
        ]}
      >
        <Input
          prefix={<UserOutlined />}
          placeholder="Enter customer name"
          size="large"
        />
      </Form.Item>

      <Form.Item
        name="phone"
        label="Phone Number"
        rules={[
          {
            validator: validateSouthAfricanPhone,
          },
        ]}
      >
        <Input
          prefix={<PhoneOutlined />}
          placeholder="Enter phone number (optional)"
          size="large"
        />
      </Form.Item>

      <Form.Item
        name="email"
        label="Email Address"
        rules={[
          { type: "email", message: "Please enter a valid email address" },
        ]}
      >
        <Input
          prefix={<MailOutlined />}
          placeholder="Enter email address (optional)"
          size="large"
        />
      </Form.Item>

      <Form.Item
        name="address"
        label="Address"
      >
        <TextArea
          placeholder="Enter address (optional)"
          rows={3}
        />
      </Form.Item>
    </Form>
  );
};

export default CustomerForm;

